import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { LocalNotification } from '@amzn/aac-chrome-internal-api';
import { RootState } from 'src/app/store';
import { useAcesBanners, useAcesBannersV2 } from '../useAcesBanners.hook';
import {
  BANNER_LEVEL,
  BANNER_TYPE,
  PAGE_LAYOUT_TYPE,
  selectPageLayoutType,
  selectPageLevelBanners,
} from 'src/app/slice';
import {
  bannerLocalStorageKey,
  bannerPlacementType,
  SwitcherGlobalAccount,
  UseAcesBannersResponse,
} from '../types';
import { ConnectedBanner } from './Banner';
import { selectHieConsoleLevelBannerCdn } from 'src/features/header/store/slice';
import { PageBannerContainer } from '../styles';
import { PageHeaderWrapper } from '../../PageHeader/styles';
import { useStardustBanners } from '../useStardustBanners.hook';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import useAccountInfo from 'src/commons/hooks/useAccountInfo';
import { WeblabedFeatures } from 'src/commons/constants';
import { useHeaderBundle } from 'src/providers/PantherLocalizationProvider';
import { unsupportedBrowserBanner } from 'src/constants/unsupported-browser-banner';
import { containsUnsupportedCSS } from 'src/utils/browser';

interface BannerWithAcesProps {
  banners: LocalNotification[];
  bannerLevel: BANNER_LEVEL;
  cloudfrontURL: string;
  isMobileDevice?: boolean;
  withPageHeaderWrapper?: boolean;
}

const ACES_REQUEST_TIMEOUT = 1000;

/**
 * Resolves the banner source for both global (console) and page banners, mounting
 * exactly one banner source component so only one aces notifications hook (v1
 * CHFS or v2 WonderwallService) is ever called. Splitting on a component boundary
 * keeps each variant hook call unconditional (Rules of Hooks) and guarantees no
 * double-fetch.
 *
 * V1 (legacy CHFS) is the default and serves as the weblab Control. The
 * NOTIFICATIONS_WWS_DIRECT Treatment (flag on) switches to the V2
 * WonderwallService-direct path — i.e. Control = V1, Treatment = V2.
 */
export const BannerWithAces: React.VFC<BannerWithAcesProps> = (props) => {
  const useV2WwsDirect = useIsFeatureEnabled(
    WeblabedFeatures.NOTIFICATIONS_WWS_DIRECT,
  );

  return useV2WwsDirect ? (
    <AcesBannerSourceV2 {...props} />
  ) : (
    <AcesBannerSourceV1 {...props} />
  );
};

// Legacy path: banners via CHFS/MONS (useNotifications).
const AcesBannerSourceV1: React.VFC<BannerWithAcesProps> = (props) => {
  const aces = useAcesBanners(bannerPlacementType[props.bannerLevel]);
  return <BannerWithAcesView {...props} aces={aces} />;
};

// Placeholder aces response used while account info is still loading, so the
// view renders (loading) without a banner source hook being mounted yet.
const LOADING_ACES: UseAcesBannersResponse = {
  notifications: [],
  loading: true,
  actions: {
    refresh: () => undefined,
    display: () => Promise.resolve(),
    read: () => Promise.resolve(),
    readSubNotification: () => Promise.resolve(),
    dismiss: () => Promise.resolve(),
  },
};

// WWS-direct path: banners via WonderwallService (useNotificationsV2).
//
// useNotificationsV2 refetches whenever accountParams changes, and the Single
// Global Ad Account ID (SGA) comes from useAccountInfo(), which resolves
// asynchronously (undefined at first). Mounting the fetch hook before it lands
// fires a first request without the SGA, then a second once it arrives. So we
// hold the fetching component off until useAccountInfo has loaded and pass the
// resolved SGA in together with the member entity the switcher resolved it for;
// the first (and only) request then carries the SGA when that entity is the
// page's own.
const AcesBannerSourceV2: React.VFC<BannerWithAcesProps> = (props) => {
  const { globalAccountId, currentEntityId, isLoading } = useAccountInfo();

  if (isLoading) {
    return <BannerWithAcesView {...props} aces={LOADING_ACES} />;
  }

  return (
    <AcesBannerSourceV2Ready
      {...props}
      switcherGlobalAccount={{
        globalAccountId,
        memberEntityId: currentEntityId,
      }}
    />
  );
};

// Inner component mounted only once account info has resolved, so useAcesBannersV2
// (and thus useNotificationsV2) fires a single request already carrying the SGA.
const AcesBannerSourceV2Ready: React.VFC<
  BannerWithAcesProps & { switcherGlobalAccount: SwitcherGlobalAccount }
> = ({ switcherGlobalAccount, ...props }) => {
  const aces = useAcesBannersV2(
    bannerPlacementType[props.bannerLevel],
    switcherGlobalAccount,
  );
  return <BannerWithAcesView {...props} aces={aces} />;
};

interface BannerWithAcesViewProps extends BannerWithAcesProps {
  aces: UseAcesBannersResponse;
}

const BannerWithAcesView: React.VFC<BannerWithAcesViewProps> = ({
  banners,
  bannerLevel,
  cloudfrontURL,
  isMobileDevice = false,
  withPageHeaderWrapper = false,
  aces,
}) => {
  const pageLayoutType = useSelector(selectPageLayoutType);
  const bundle = useHeaderBundle();
  const isStardustBannerFeatureEnabled = useIsFeatureEnabled(
    WeblabedFeatures.STARDUST_BANNER_UI,
  );
  const isFocusLayout = pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS;

  if (bannerLevel === BANNER_LEVEL.CONSOLE && containsUnsupportedCSS()) {
    banners = [
      {
        id: unsupportedBrowserBanner.id,
        type: unsupportedBrowserBanner.type as BANNER_TYPE,
        message: bundle.getMessage(unsupportedBrowserBanner.message),
        link: {
          label: bundle.getMessage(unsupportedBrowserBanner.link.label),
          url: unsupportedBrowserBanner.link.url,
        },
      },
    ];
  }

  const stardustBanners = useStardustBanners(
    bannerPlacementType[bannerLevel],
    cloudfrontURL,
    isStardustBannerFeatureEnabled,
  );

  const { notifications, loading, actions } = aces;

  const [shouldRender, setShouldRender] = useState(false);

  const combinedBanners = useMemo(() => {
    const localStorageItems = JSON.parse(
      localStorage.getItem(bannerLocalStorageKey[bannerLevel]) || '[]',
    );
    const returnedBanners = [
      ...banners,
      ...notifications,
      ...stardustBanners.notifications,
    ].filter((banner) => !localStorageItems.includes(banner.id));

    return returnedBanners;
  }, [banners, notifications, stardustBanners]);

  // Set timeout for getting aces notifications
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, ACES_REQUEST_TIMEOUT);
    return () => clearTimeout(timer);
  }, []);

  // Do not render if aces call is still in progress and timeout not reached
  if (loading && !shouldRender) {
    return null;
  }

  if (combinedBanners.length === 0) {
    return null;
  }

  const renderBanner = () => (
    <ConnectedBanner
      banners={combinedBanners}
      acesDismissFn={actions.dismiss}
      bannerLevel={bannerLevel}
    />
  );

  // Render page level banner
  if (bannerLevel === BANNER_LEVEL.PAGE) {
    if (withPageHeaderWrapper) {
      return (
        <PageHeaderWrapper
          id="pageHeader"
          data-ccx-e2e-id="aac-pageheader"
          isMobileDevice={isMobileDevice}
          hasSectionNav={false}
        >
          <PageBannerContainer isFocusLayout={isFocusLayout}>
            {renderBanner()}
          </PageBannerContainer>
        </PageHeaderWrapper>
      );
    }

    return (
      <PageBannerContainer isFocusLayout={isFocusLayout}>
        {renderBanner()}
      </PageBannerContainer>
    );
  }

  // Render console level banner
  return renderBanner();
};

const ConsoleLevelBannerWithAcesConnector = connect(
  (state: RootState, ownProps) => {
    return {
      ...ownProps,
      banners: [],
      bannerLevel: BANNER_LEVEL.CONSOLE,
      cloudfrontURL: selectHieConsoleLevelBannerCdn(state),
    };
  },
);

export const ConnectedConsoleLevelBannerWithAces =
  ConsoleLevelBannerWithAcesConnector(BannerWithAces);

const PageLevelBannerWithAcesConnector = connect(
  (state: RootState, ownProps) => {
    return {
      ...ownProps,
      banners: selectPageLevelBanners(state),
      bannerLevel: BANNER_LEVEL.PAGE,
      cloudfrontURL: selectHieConsoleLevelBannerCdn(state),
    };
  },
);

export const ConnectedPageLevelBannersWithAces =
  PageLevelBannerWithAcesConnector(BannerWithAces);
